import {
  Autocomplete,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
} from "@mui/material";
import InputField from "components/Input/InputField";
import useKothar from "context/useKothar";
import { useEffect, useMemo, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useCreateEmployee, useEmployeeById, useUpdateEmployee } from "utils/employeeService";
import { canAccessPlatformOrganisations } from "utils/permissionUtils";

const ROLE_OPTIONS = [
  { value: "EMPLOYEE", label: "Employee" },
  { value: "MANAGER", label: "Manager" },
  { value: "SUPERVISOR", label: "Supervisor" },
];

const DEPARTMENT_OPTIONS = [
  "Admissions",
  "Accounts",
  "Visa",
  "Marketing",
  "Student Services",
  "Administration",
  "Other",
];

const sectionClass = "rounded-xl border border-app-border bg-app-surface p-5 shadow-sm";
const sectionTitleClass = "text-base font-semibold text-app-text mb-1";
const sectionHintClass = "text-sm text-app-text-secondary mb-4";

const AddEmployee = () => {
  const [data, setData] = useState({
    name: "",
    contact: "",
    email: "",
    pin: "",
    startDate: "",
    jobTitle: "",
    department: "",
    status: "ACTIVE",
    employmentType: "FULL_TIME",
    payType: "MONTHLY",
    monthlySalary: "",
    expectedHoursPerMonth: "40",
    hourlyRate: "",
    overtimeEligible: false,
    isFixedHours: false,
    fixedHours: "",
    role: "EMPLOYEE",
    orgId: null,
    orgName: "",
  });

  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [{ consultancyList }, { refetchConsultancy }] = useKothar();

  const activeOrgId = useMemo(() => {
    try {
      const detail = JSON.parse(localStorage.getItem("userDetail"));
      return detail?.activeOrgId || detail?.orgId || null;
    } catch {
      return null;
    }
  }, []);

  const canPickAnyOrg = canAccessPlatformOrganisations();
  const orgOptions = useMemo(() => {
    const list = consultancyList || [];
    if (canPickAnyOrg || list.length <= 1) return list;
    if (activeOrgId) {
      const scoped = list.filter((item) => item.id === activeOrgId);
      return scoped.length ? scoped : list;
    }
    return list;
  }, [consultancyList, activeOrgId, canPickAnyOrg]);

  const selectedOrg = orgOptions.find((item) => item?.id === data?.orgId) || null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const { data: employeeData, refetch: refetchEmployeeData } = useEmployeeById(id, {
    enabled: !!id,
  });

  const createEmployeeMutation = useCreateEmployee({
    onSuccess: () => {
      toast.success("Employee added successfully");
      navigate("/admin/timesheets");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.errorMessage ?? "Error adding employee");
    },
  });

  const updateEmployeeMutation = useUpdateEmployee({
    onSuccess: () => {
      toast.success("Employee updated successfully");
      navigate("/admin/timesheets");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.errorMessage ?? "Error updating employee");
    },
  });

  useEffect(() => {
    refetchConsultancy();
  }, [refetchConsultancy]);

  useEffect(() => {
    if (!data?.id && !data?.orgId && activeOrgId) {
      const match = orgOptions.find((item) => item.id === activeOrgId);
      setData((prev) => ({
        ...prev,
        orgId: activeOrgId,
        orgName: match?.name || "",
      }));
    }
  }, [activeOrgId, data?.id, data?.orgId, orgOptions]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!data?.orgId) {
      toast.error("Please select an organisation");
      return;
    }
    if (!/^\d{4,6}$/.test(String(data?.pin || "").trim())) {
      toast.error("PIN must be 4 to 6 digits");
      return;
    }

    const isMonthly = data?.payType !== "HOURLY";
    if (isMonthly && !(Number(data?.monthlySalary) > 0)) {
      toast.error("Enter a monthly salary");
      return;
    }
    if (!isMonthly && !(Number(data?.hourlyRate) > 0)) {
      toast.error("Enter an hourly rate");
      return;
    }

    const payload = {
      name: data?.name?.trim(),
      contact: data?.contact,
      email: data?.email?.trim(),
      pin: String(data?.pin).trim(),
      startDate: data?.startDate ? new Date(data?.startDate).toISOString() : null,
      jobTitle: data?.jobTitle?.trim(),
      department: data?.department?.trim(),
      status: data?.status === "INACTIVE" ? "INACTIVE" : "ACTIVE",
      employmentType: data?.employmentType === "PART_TIME" ? "PART_TIME" : "FULL_TIME",
      payType: isMonthly ? "MONTHLY" : "HOURLY",
      monthlySalary: isMonthly ? Number(data?.monthlySalary) : null,
      expectedHoursPerMonth:
        isMonthly && data?.expectedHoursPerMonth !== ""
          ? Number(data?.expectedHoursPerMonth)
          : null,
      hourlyRate: isMonthly ? null : Number(data?.hourlyRate),
      overtimeEligible: Boolean(data?.overtimeEligible),
      isFixedHours: isMonthly ? true : Boolean(data?.isFixedHours),
      fixedHours: isMonthly
        ? String(data?.expectedHoursPerMonth || "")
        : String(data?.isFixedHours ? data?.fixedHours : ""),
      role: String(data?.role || "EMPLOYEE"),
      orgId: data?.orgId,
      organizationId: data?.orgId,
      orgName: data?.orgName || selectedOrg?.name || "",
    };

    if (data?.id) {
      updateEmployeeMutation.mutate({ id: data?.id, employeeData: payload });
    } else {
      createEmployeeMutation.mutate(payload);
    }
  };

  useEffect(() => {
    if (employeeData) {
      setData({
        name: employeeData?.name || "",
        contact: employeeData?.contact || "",
        email: employeeData?.email || "",
        pin: employeeData?.pin || "",
        startDate: employeeData?.startDate ? String(employeeData?.startDate).split("T")[0] : "",
        jobTitle: employeeData?.jobTitle || "",
        department: employeeData?.department || "",
        status: employeeData?.status || "ACTIVE",
        employmentType: employeeData?.employmentType || "FULL_TIME",
        payType: employeeData?.payType || (employeeData?.monthlySalary ? "MONTHLY" : "HOURLY"),
        monthlySalary: employeeData?.monthlySalary || "",
        expectedHoursPerMonth:
          employeeData?.expectedHoursPerMonth || employeeData?.fixedHours || "208",
        hourlyRate: employeeData?.hourlyRate || "",
        overtimeEligible: employeeData?.overtimeEligible || false,
        isFixedHours: employeeData?.isFixedHours || false,
        fixedHours: employeeData?.fixedHours || "",
        id: employeeData?.id,
        role: employeeData?.role || "EMPLOYEE",
        orgId:
          employeeData?.orgId ||
          employeeData?.organizationId ||
          employeeData?.organization?.id ||
          null,
        orgName: employeeData?.orgName || employeeData?.organization?.name || "",
      });
    }
  }, [employeeData]);

  useEffect(() => {
    if (state?.item || id) {
      refetchEmployeeData();
    }
  }, [state, id, refetchEmployeeData]);

  const isSubmitting =
    createEmployeeMutation.isLoading ||
    createEmployeeMutation.isPending ||
    updateEmployeeMutation.isLoading ||
    updateEmployeeMutation.isPending;

  const isEdit = Boolean(data?.id);

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text">
          <div className="rounded-t mb-0 px-6 py-4 border-b border-app-border">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => navigate("/admin/timesheets")}
                  className="text-app-text hover:bg-app-surface-hover"
                  startIcon={<IoArrowBack />}
                >
                  Back
                </Button>
                <div>
                  <h3 className="font-semibold text-xl text-app-text">
                    {isEdit ? "Edit Employee" : "Add Employee"}
                  </h3>
                  <p className="text-sm text-app-text-secondary">
                    Register staff for clock-in, timesheets, and pay tracking.
                  </p>
                </div>
              </div>
              <Chip
                size="small"
                color={data.status === "ACTIVE" ? "success" : "default"}
                label={data.status === "ACTIVE" ? "Active" : "Inactive"}
                variant="outlined"
              />
            </div>
          </div>

          <div className="block w-full overflow-x-auto">
            <form className="flex-auto lg:px-8 py-6 space-y-5" onSubmit={handleSubmit}>
              <div className={sectionClass}>
                <h4 className={sectionTitleClass}>Organisation</h4>
                <p className={sectionHintClass}>
                  This employee will clock in and appear on timesheets for the selected
                  organisation.
                </p>
                <Autocomplete
                  disablePortal
                  options={orgOptions}
                  value={selectedOrg}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  onChange={(_, value) =>
                    setData((prev) => ({
                      ...prev,
                      orgId: value?.id || null,
                      orgName: value?.name || "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Organisation"
                      required
                      placeholder="Select organisation"
                    />
                  )}
                />
                {!canPickAnyOrg && orgOptions.length <= 1 ? (
                  <FormHelperText>Organisation is set from your current workspace.</FormHelperText>
                ) : null}
              </div>

              <div className={sectionClass}>
                <h4 className={sectionTitleClass}>Personal details</h4>
                <p className={sectionHintClass}>
                  Used on the public clock-in kiosk and timesheet records.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <InputField
                    label="Full name"
                    placeholder="e.g. Priya Sharma"
                    name="name"
                    required
                    type="text"
                    value={data?.name}
                    onChange={handleInputChange}
                  />
                  <InputField
                    type="email"
                    placeholder="name@organisation.com"
                    name="email"
                    label="Email"
                    required
                    value={data?.email}
                    onChange={handleInputChange}
                  />
                  <InputField
                    type="text"
                    placeholder="04xx xxx xxx"
                    name="contact"
                    label="Contact number"
                    required
                    value={data?.contact}
                    onChange={handleInputChange}
                  />
                  <div>
                    <InputField
                      type="text"
                      placeholder="4–6 digit PIN"
                      name="pin"
                      label="Clock-in PIN"
                      required
                      value={data?.pin}
                      onChange={handleInputChange}
                      maxLength={6}
                    />
                    <p className="text-xs text-app-text-secondary mt-1">
                      Used on the public clock-in page. Keep it unique.
                    </p>
                  </div>
                </div>
              </div>

              <div className={sectionClass}>
                <h4 className={sectionTitleClass}>Employment</h4>
                <p className={sectionHintClass}>Role and where they sit in the organisation.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <InputField
                    type="date"
                    name="startDate"
                    label="Start date"
                    value={data?.startDate || ""}
                    onChange={handleInputChange}
                  />
                  <InputField
                    type="text"
                    placeholder="e.g. Admissions Officer"
                    name="jobTitle"
                    label="Job title"
                    value={data?.jobTitle}
                    onChange={handleInputChange}
                  />
                  <Autocomplete
                    freeSolo
                    options={DEPARTMENT_OPTIONS}
                    value={data?.department || ""}
                    onChange={(_, value) =>
                      setData((prev) => ({ ...prev, department: value || "" }))
                    }
                    onInputChange={(_, value) =>
                      setData((prev) => ({ ...prev, department: value }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Department" placeholder="Select or type" />
                    )}
                  />
                  <Autocomplete
                    options={ROLE_OPTIONS}
                    value={
                      ROLE_OPTIONS.find((item) => item.value === data?.role) || ROLE_OPTIONS[0]
                    }
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    onChange={(_, value) =>
                      setData((prev) => ({ ...prev, role: value?.value || "EMPLOYEE" }))
                    }
                    renderInput={(params) => <TextField {...params} label="Timesheet role" />}
                  />
                  <FormControl>
                    <FormLabel className="text-app-text-secondary uppercase text-xs font-bold mb-2">
                      Employment type
                    </FormLabel>
                    <RadioGroup
                      row
                      name="employmentType"
                      value={data?.employmentType || "FULL_TIME"}
                      onChange={(e) => {
                        const employmentType = e.target.value;
                        setData((prev) => ({
                          ...prev,
                          employmentType,
                          payType: employmentType === "FULL_TIME" ? "MONTHLY" : prev.payType,
                          expectedHoursPerMonth:
                            employmentType === "FULL_TIME"
                              ? prev.expectedHoursPerMonth || "208"
                              : prev.expectedHoursPerMonth || "80",
                        }));
                      }}
                    >
                      <FormControlLabel value="FULL_TIME" control={<Radio />} label="Full-time" />
                      <FormControlLabel value="PART_TIME" control={<Radio />} label="Part-time" />
                    </RadioGroup>
                    <FormHelperText>
                      Nepal office staff are usually full-time on a monthly salary.
                    </FormHelperText>
                  </FormControl>
                  <FormControl>
                    <FormLabel className="text-app-text-secondary uppercase text-xs font-bold mb-2">
                      Status
                    </FormLabel>
                    <RadioGroup
                      row
                      name="status"
                      value={data?.status || "ACTIVE"}
                      onChange={(e) => setData((prev) => ({ ...prev, status: e.target.value }))}
                    >
                      <FormControlLabel value="ACTIVE" control={<Radio />} label="Active" />
                      <FormControlLabel value="INACTIVE" control={<Radio />} label="Inactive" />
                    </RadioGroup>
                    <FormHelperText>Inactive staff cannot clock in.</FormHelperText>
                  </FormControl>
                </div>
              </div>

              <div className={sectionClass}>
                <h4 className={sectionTitleClass}>Pay settings</h4>
                <p className={sectionHintClass}>
                  Full-time Nepal staff are paid a monthly salary. Clock-in still tracks attendance
                  against expected hours.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <FormControl>
                    <FormLabel className="text-app-text-secondary uppercase text-xs font-bold mb-2">
                      Pay basis
                    </FormLabel>
                    <RadioGroup
                      row
                      name="payType"
                      value={data?.payType || "MONTHLY"}
                      onChange={(e) => setData((prev) => ({ ...prev, payType: e.target.value }))}
                    >
                      <FormControlLabel
                        value="MONTHLY"
                        control={<Radio />}
                        label="Monthly salary"
                      />
                      <FormControlLabel value="HOURLY" control={<Radio />} label="Hourly" />
                    </RadioGroup>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(data?.overtimeEligible)}
                        onChange={(e) =>
                          setData((prev) => ({ ...prev, overtimeEligible: e.target.checked }))
                        }
                      />
                    }
                    label="Overtime eligible"
                  />

                  {data?.payType !== "HOURLY" ? (
                    <>
                      <InputField
                        type="number"
                        placeholder="e.g. 45000"
                        name="monthlySalary"
                        label="Monthly salary"
                        required
                        value={data?.monthlySalary}
                        onChange={handleInputChange}
                        inputProps={{ min: 0, step: "0.01" }}
                      />
                      <div>
                        <InputField
                          type="number"
                          placeholder="e.g. 40"
                          name="expectedHoursPerMonth"
                          label="Expected hours / week"
                          value={data?.expectedHoursPerMonth}
                          onChange={handleInputChange}
                          inputProps={{ min: 0, step: "1" }}
                        />
                        <p className="text-xs text-app-text-secondary mt-1">
                          Typical full-time: 8 hours × 5 days = 40 hours. Used to compare clocked
                          hours against the monthly salary.
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <InputField
                        type="number"
                        placeholder="e.g. 38"
                        name="hourlyRate"
                        label="Hourly rate"
                        required
                        value={data?.hourlyRate}
                        onChange={handleInputChange}
                        inputProps={{ min: 0, step: "0.01" }}
                      />
                      <div className="flex flex-col justify-center">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(data?.isFixedHours)}
                              onChange={(e) =>
                                setData((prev) => ({
                                  ...prev,
                                  isFixedHours: e.target.checked,
                                  fixedHours: e.target.checked ? prev.fixedHours : "",
                                }))
                              }
                            />
                          }
                          label="Fixed weekly hours"
                        />
                      </div>
                      {data?.isFixedHours && (
                        <InputField
                          type="number"
                          placeholder="e.g. 38"
                          name="fixedHours"
                          label="Fixed hours per week"
                          value={data?.fixedHours}
                          onChange={handleInputChange}
                          inputProps={{ min: 0, step: "0.5" }}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outlined" onClick={() => navigate("/admin/timesheets")}>
                  Cancel
                </Button>
                <Button variant="contained" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : isEdit ? "Update employee" : "Add employee"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEmployee;
